import type { TrendPeriodScores } from "@repo/app-types/entities/attainmentTrend";
import {
  buildTeammateDisplayNames,
  type Teammate,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { getCriteriaTrendMovementCategory } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { recordEntries } from "@repo/app-types/helpers/utilities";
import { useMemo, type FC } from "react";
import {
  computeScoreAxisRange,
  formatScore,
  formatScoreAxisLabel,
  formatScoreDelta,
  ScoreMovementScatterChart,
  type ScoreMovementPoint,
} from "../scoreMovementScatterChart.js";

interface ScoreMovementChartProps {
  readonly scores: Record<TeammateId, TrendPeriodScores>;
  readonly teammates: readonly Teammate[];
  readonly gradingValueType: "yes_no" | "1_5_score";
}

export const ScoreMovementChart: FC<ScoreMovementChartProps> = ({
  scores,
  teammates,
  gradingValueType,
}) => {
  const teammatesMap = useMemo(() => {
    const map: Record<TeammateId, Teammate> = {};
    for (const teammate of teammates) {
      map[teammate.id] = teammate;
    }
    return map;
  }, [teammates]);

  const chartPoints = useMemo((): readonly ScoreMovementPoint[] => {
    const result: ScoreMovementPoint[] = [];

    for (const [teammateId, trendScores] of recordEntries(scores)) {
      const startScore = trendScores.starting_score;
      const currentScore = trendScores.current_score;

      if (startScore === null || currentScore === null) {
        continue;
      }

      const teammate = teammatesMap[teammateId];
      const displayName = teammate
        ? buildTeammateDisplayNames(teammate).primaryDisplayName
        : teammateId;

      const delta = currentScore - startScore;
      const category = getCriteriaTrendMovementCategory(
        delta,
        gradingValueType,
      );

      result.push({
        id: teammateId,
        startScore,
        currentScore,
        category,
        tooltipContent: `${displayName}\nBefore: ${formatScore(startScore, gradingValueType)}\nAfter: ${formatScore(currentScore, gradingValueType)}\nDelta: ${formatScoreDelta(delta, gradingValueType)}`,
      });
    }

    return result;
  }, [scores, teammatesMap, gradingValueType]);

  const { axisMin, axisMax, axisTicks } = useMemo(
    () => computeScoreAxisRange(chartPoints, gradingValueType),
    [chartPoints, gradingValueType],
  );

  return (
    <ScoreMovementScatterChart
      points={chartPoints}
      axisMin={axisMin}
      axisMax={axisMax}
      axisTicks={axisTicks}
      formatAxisLabel={(tick) => formatScoreAxisLabel(tick, gradingValueType)}
      xAxisTitle="Starting score"
      yAxisTitle="Ending score"
    />
  );
};
